import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { Box, Collapse, List, ListItemButton, ListItemText, Typography } from '@mui/material';
import color from '@theme/Colors';
import { ReactNode, useState } from 'react';

export type MenuItem = {
    name: string;
    id: string;
    subItems?: MenuItem[];
    desc?: ReactNode;
    highlight?: boolean;
};

export type MenuItems = {
    title: string;
    items: MenuItem[];
};

type BaseMenuListProps<T extends string> = {
    menuItems: MenuItems[];
    onSetIsOpenCollapse?: (updateFn: (prevState: { [key in T]: boolean }) => { [key in T]: boolean }) => void;
    menusOpen?: { [key in T]: boolean };
};

const BaseMenuListCollapse = <T extends string>({
    menuItems,
    menusOpen,
    onSetIsOpenCollapse,
}: BaseMenuListProps<T>) => {
    const [selectedItem, setSelectedItem] = useState<string>(menuItems[0].items[0].name);
    const [openMenus, setOpenMenus] = useState<{ [key: string]: boolean }>(
        menusOpen ?? {
            0: true,
        }
    );

    const handleClick = (index: string) => {
        setOpenMenus((prev) => ({
            ...prev,
            [index]: !prev[index],
        }));
    };

    const handleItemClick = (itemName: string, itemId?: T) => {
        setSelectedItem(itemName);

        const element = document.getElementById(itemId ?? '');
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });

            if (itemId) {
                onSetIsOpenCollapse?.((prev) => ({ ...prev, [itemId]: true }));
            }
        }
    };

    const isChildSelected = (subItems: MenuItem[]): boolean => {
        return subItems.some((item) => {
            if (item.name === selectedItem) return true;
            if (item.subItems) return isChildSelected(item.subItems);
            return false;
        });
    };

    const renderMenuItems = (items: MenuItem[], parentIndex: string) => {
        return items.map((item, index) => {
            const key = `${parentIndex}-${index}`;
            const hasSubItems = item.subItems && item.subItems.length > 0;
            const childSelected = hasSubItems && isChildSelected(item.subItems!);
            const itemSelected = selectedItem === item.name || childSelected;

            return (
                <Box key={key}>
                    <ListItemButton
                        sx={{
                            borderRadius: 1,
                            mb: 1,
                            py: '4px',
                            '&.Mui-selected, &.Mui-selected:hover': {
                                bgcolor: itemSelected ? '#E5F5ED' : undefined,
                            },
                            '&:hover': {
                                bgcolor: color.grey100,
                            },
                        }}
                        selected={itemSelected}
                        onClick={() => {
                            handleItemClick(item.name, item.id as T);
                            handleClick(key);
                        }}
                    >
                        <ListItemText
                            primary={item.name}
                            sx={{
                                '& .MuiTypography-root': {
                                    color: itemSelected && item.highlight 
                                        ? color.danger 
                                        : itemSelected 
                                        ? color.success 
                                        : item.highlight 
                                        ? color.danger 
                                        : color.textPrimary,
                                },
                            }}
                        />
                        {hasSubItems &&
                            (openMenus[key] ? <ExpandLess sx={{ width: 16 }} /> : <ExpandMore sx={{ width: 16 }} />)}
                    </ListItemButton>
                    {hasSubItems && (
                        <Collapse in={openMenus[key]} unmountOnExit>
                            <List sx={{ px: 2 }} disablePadding>
                                {renderMenuItems(item.subItems!, key)}
                            </List>
                        </Collapse>
                    )}
                </Box>
            );
        });
    };

    return (
        <List sx={{ width: '100%', backgroundColor: 'inherit' }}>
            {menuItems.map((menu, index) => (
                <Box key={index}>
                    {menu.title && (
                        <ListItemButton
                            onClick={() => handleClick(`${index}`)}
                            sx={{
                                justifyContent: 'space-between',
                                borderRadius: 1,
                                margin: '0px 8px 8px 8px',
                            }}
                        >
                            <Typography variant="h5">{menu.title}</Typography>
                            {openMenus[`${index}`] ? (
                                <ExpandLess sx={{ width: 16 }} />
                            ) : (
                                <ExpandMore sx={{ width: 16 }} />
                            )}
                        </ListItemButton>
                    )}
                    <Collapse in={openMenus[`${index}`]} timeout="auto" unmountOnExit>
                        <List sx={{ px: 2 }} disablePadding>
                            {renderMenuItems(menu.items, `${index}`)}
                        </List>
                    </Collapse>
                </Box>
            ))}
        </List>
    );
};

export default BaseMenuListCollapse;
